import React from 'react';
import { DAYS } from './constants';
import { TIME_OPTIONS, DEFAULT_DAY_TIME } from './timeOptions';

// Schedule sub-section of MeetStep. Matches the prototype layout exactly:
//   - "When does the table meet?" h3
//   - Two-column flex container: day buttons + TBD pill on the LEFT (vertical
//     column), per-day time pickers in a 2-col grid on the RIGHT
//   - Selecting a day defaults its time to 19:00; checking "Schedule is TBD"
//     clears daySchedule and disables the day buttons
// Ported from prototypes/campaign-creation-prototype.jsx lines 1661-1840.
export const ScheduleStep = ({ form, update, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};

  const toggleDay = (key) => {
    if (form.scheduleTBD) return;
    const next = { ...form.daySchedule };
    if (next[key] != null) {
      delete next[key];
    } else {
      next[key] = DEFAULT_DAY_TIME;
    }
    update({ daySchedule: next, scheduleTBD: false });
  };

  const setDayTime = (key, value) => {
    update({ daySchedule: { ...form.daySchedule, [key]: value } });
  };

  const selectedDays = DAYS.filter((day) => form.daySchedule[day.key] != null);
  const tbdDisabled = form.scheduleTBD;
  const accentLightHex = (c.accentLight || '#ffffff').replace('#', '');
  const chevronBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 12 8'%3E%3Cpath fill='%23${accentLightHex}' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`;

  return (
    <div>
      <h3 style={{
        margin: '0 0 16px',
        fontFamily: t.headingFamily,
        fontSize: '1.4rem',
        fontWeight: 600,
        color: c.accentLight,
        letterSpacing: '0.01em',
        lineHeight: 1.2,
      }}>When does the table meet?</h3>

      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 48,
        marginBottom: 14,
        flexWrap: 'wrap',
      }}>
        {/* Left column — day squares + TBD pill stacked vertically. The
            TBD chip is anchored under the squares so it stays put as the
            time grid on the right grows. */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 16,
          flex: '0 0 auto',
        }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DAYS.map((day) => {
              const selected = form.daySchedule[day.key] != null;
              return (
                <button
                  key={day.key}
                  type="button"
                  onClick={() => toggleDay(day.key)}
                  title={day.full}
                  disabled={tbdDisabled}
                  aria-pressed={selected}
                  aria-label={day.full}
                  style={{
                    width: 38, height: 38,
                    background: selected ? c.accent : 'transparent',
                    border: `1.5px solid ${selected ? c.accent : c.borderPrimary}`,
                    borderRadius: 6,
                    color: selected ? c.textOnAccent : (tbdDisabled ? c.textMuted : c.textSecondary),
                    fontFamily: t.headingFamily,
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    cursor: tbdDisabled ? 'not-allowed' : 'pointer',
                    opacity: tbdDisabled ? 0.4 : 1,
                    transition: 'all 150ms ease',
                    boxShadow: selected ? `0 0 10px ${c.accentGlow}` : 'none',
                  }}
                >{day.label}</button>
              );
            })}
          </div>

          <label style={{
            alignSelf: 'flex-start',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 14px',
            background: form.scheduleTBD ? c.bgHover : 'transparent',
            border: `1px solid ${form.scheduleTBD ? c.accent : c.borderPrimary}`,
            borderRadius: 999,
            cursor: 'pointer',
            fontFamily: t.fontFamily,
            fontSize: '0.86rem',
            color: form.scheduleTBD ? c.accentLight : c.textSecondary,
            transition: 'all 120ms ease',
            userSelect: 'none',
          }}>
            <input
              type="checkbox"
              checked={form.scheduleTBD}
              onChange={(ev) => update({
                scheduleTBD: ev.target.checked,
                daySchedule: ev.target.checked ? {} : form.daySchedule,
              })}
              style={{ display: 'none' }}
            />
            <span style={{
              width: 14, height: 14,
              border: `1.5px solid ${form.scheduleTBD ? c.accent : c.borderPrimary}`,
              background: form.scheduleTBD ? c.accent : 'transparent',
              borderRadius: 3,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flex: '0 0 14px',
              transition: 'all 120ms ease',
            }}>
              {form.scheduleTBD && (
                <span style={{ color: c.textOnAccent, fontSize: '0.66rem', fontWeight: 700, lineHeight: 1 }}>✓</span>
              )}
            </span>
            Schedule is TBD
          </label>
        </div>

        {/* Right column — per-day time pickers in a 2-col grid. */}
        {!tbdDisabled && selectedDays.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, auto)',
            columnGap: 28,
            rowGap: 8,
            alignItems: 'center',
            flex: '0 0 auto',
          }}>
            {selectedDays.map((day) => (
              <div key={day.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  fontFamily: t.fontFamily,
                  fontSize: '0.85rem',
                  color: c.textSecondary,
                  fontWeight: 500,
                  minWidth: 32,
                }}>{day.short}</div>
                <select
                  value={form.daySchedule[day.key]}
                  onChange={(ev) => setDayTime(day.key, ev.target.value)}
                  aria-label={`Time on ${day.full}`}
                  style={{
                    padding: '5px 22px 5px 10px',
                    background: c.bgInput,
                    border: `1px solid ${c.borderLight}`,
                    borderRadius: 6,
                    color: c.textPrimary,
                    fontSize: '0.85rem',
                    fontFamily: t.fontFamily,
                    outline: 'none',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: chevronBg,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    minWidth: 88,
                  }}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} style={{ background: c.bgCardSolid }}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
